import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@/auth/token-storage';

export interface StreamChunk {
  type: 'text' | 'tool_call' | 'tool_call_delta' | 'tool_execution' | 'usage' | 'done' | 'error';
  content?: string;
  toolCall?: { id: string; name: string; arguments: string };
  toolExecution?: { name: string; executionTimeMs: number };
  usage?: { promptTokens: number; completionTokens: number; totalTokens: number };
  totalUsage?: { promptTokens: number; completionTokens: number; totalTokens: number };
  totalCost?: number;
  error?: string;
}

export interface ToolExecutionItem {
  name: string;
  executionTimeMs: number;
}

export interface StreamingState {
  isStreaming: boolean;
  streamedContent: string;
  streamedToolExecutions: ToolExecutionItem[];
  streamedTotalTokens: number;
  streamedTotalCost: number;
  error: string | null;
}

export function useStreamingChat() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<StreamingState>({
    isStreaming: false,
    streamedContent: '',
    streamedToolExecutions: [],
    streamedTotalTokens: 0,
    streamedTotalCost: 0,
    error: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setState((prev) => ({ ...prev, isStreaming: false }));
    }
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, []);

  const streamMessage = useCallback(
    async (
      conversationId: number,
      content: string,
      options?: {
        model?: string;
        provider?: 'openai' | 'gemini' | 'payroll-llm';
        temperature?: number;
        onChunk?: (chunk: StreamChunk) => void;
      }
    ) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      setState({
        isStreaming: true,
        streamedContent: '',
        streamedToolExecutions: [],
        streamedTotalTokens: 0,
        streamedTotalCost: 0,
        error: null,
      });

      const params = new URLSearchParams({ content });
      if (options?.model) params.set('model', options.model);
      if (options?.provider) params.set('provider', options.provider);
      if (options?.temperature !== undefined) {
        params.set('temperature', options.temperature.toString());
      }

      const token = getAccessToken();
      const baseUrl =
        typeof import.meta.env.VITE_API_URL === 'string' &&
        import.meta.env.VITE_API_URL.trim() !== ''
          ? import.meta.env.VITE_API_URL.replace(/\/$/, '')
          : '';
      const url = `${baseUrl}/api/chat/conversations/${conversationId}/stream?${params.toString()}`;

      try {
        const response = await fetch(url, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            Accept: 'text/event-stream',
          },
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let fullContent = '';
        let done = false;

        while (!done) {
          const result = await reader.read();
          done = result.done;

          if (done) break;
          const value = result.value;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6).trim();
              if (!jsonStr) continue;

              try {
                const chunk: StreamChunk = JSON.parse(jsonStr);

                if (chunk.type === 'text' && chunk.content) {
                  fullContent += chunk.content;
                  setState((prev) => ({
                    ...prev,
                    streamedContent: fullContent,
                  }));
                }

                // Clear content whenever we see tool calls (including duplicates when we continue)
                if (chunk.type === 'tool_call' || chunk.type === 'tool_call_delta') {
                  fullContent = '';
                  setState((prev) => ({
                    ...prev,
                    streamedContent: '',
                  }));
                }

                if (chunk.type === 'tool_execution' && chunk.toolExecution) {
                  fullContent = '';
                  setState((prev) => ({
                    ...prev,
                    streamedContent: '',
                    streamedToolExecutions: [
                      ...prev.streamedToolExecutions,
                      {
                        name: chunk.toolExecution!.name,
                        executionTimeMs: chunk.toolExecution!.executionTimeMs,
                      },
                    ],
                  }));
                }

                if (chunk.type === 'usage') {
                  const totalUsage = chunk.totalUsage ?? chunk.usage;
                  const totalCost = chunk.totalCost ?? 0;
                  if (totalUsage || totalCost > 0) {
                    setState((prev) => ({
                      ...prev,
                      streamedTotalTokens: totalUsage?.totalTokens ?? prev.streamedTotalTokens,
                      streamedTotalCost: totalCost > 0 ? totalCost : prev.streamedTotalCost,
                    }));
                  }
                }

                if (chunk.type === 'error') {
                  setState((prev) => ({
                    ...prev,
                    error: chunk.error || 'Unknown error',
                  }));
                }

                options?.onChunk?.(chunk);

                if (chunk.type === 'done') {
                  const totalUsage = chunk.totalUsage ?? chunk.usage;
                  setState((prev) => ({
                    ...prev,
                    isStreaming: false,
                    ...(totalUsage && {
                      streamedTotalTokens: totalUsage.totalTokens,
                    }),
                    ...(chunk.totalCost != null &&
                      chunk.totalCost > 0 && {
                        streamedTotalCost: chunk.totalCost,
                      }),
                  }));

                  queryClient.invalidateQueries({
                    queryKey: ['chat', 'conversations', conversationId],
                  });
                  queryClient.invalidateQueries({
                    queryKey: ['chat', 'conversations'],
                  });
                }
              } catch {
                console.warn('Failed to parse SSE chunk:', jsonStr);
              }
            }
          }
        }

        setState((prev) => ({ ...prev, isStreaming: false }));
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Failed to stream';
        setState({
          isStreaming: false,
          streamedContent: '',
          streamedToolExecutions: [],
          streamedTotalTokens: 0,
          streamedTotalCost: 0,
          error: errorMessage,
        });
        throw error;
      } finally {
        abortControllerRef.current = null;
      }
    },
    [queryClient]
  );

  const reset = useCallback(() => {
    setState({
      isStreaming: false,
      streamedContent: '',
      streamedToolExecutions: [],
      streamedTotalTokens: 0,
      streamedTotalCost: 0,
      error: null,
    });
  }, []);

  return {
    ...state,
    streamMessage,
    abort,
    reset,
  };
}
